import { useEffect, useRef } from 'react';

export interface ShortcutDef {
  /** Key combo string: 'mod+k', 'esc', 'mod+enter', 'mod+s', 'mod+/', '?', 'f11', 'mod+shift+f', etc.
   *  'mod' maps to Ctrl on Windows/Linux and Cmd (Meta) on macOS.
   */
  combo: string;
  handler: (e: KeyboardEvent) => void;
  /**
   * Optional predicate; when provided, the shortcut only fires if it returns true.
   * By default combos that do NOT include 'input-ok' are blocked when focus is on
   * input/textarea/contenteditable unless the combo itself contains 'mod' or is 'esc'.
   */
  when?: () => boolean;
  /**
   * Set to true to fire even when an input/textarea/contenteditable is focused.
   * By default: combos with 'mod+' prefix and 'esc' already bypass input check.
   */
  allowInInputs?: boolean;
}

// Parse a combo string into its parts
interface ParsedCombo {
  mod: boolean;    // Ctrl/Cmd
  shift: boolean;
  alt: boolean;
  key: string;    // lowercase key
}

function parseCombo(combo: string): ParsedCombo {
  const parts = combo.toLowerCase().split('+');
  const mod = parts.includes('mod');
  const shift = parts.includes('shift');
  const alt = parts.includes('alt');
  const keyParts = parts.filter((p) => p !== 'mod' && p !== 'shift' && p !== 'alt');
  const key = keyParts.join('+') || '';
  return { mod, shift, alt, key };
}

function matchesEvent(parsed: ParsedCombo, e: KeyboardEvent): boolean {
  const isMod = e.ctrlKey || e.metaKey;
  if (parsed.mod !== isMod) return false;
  if (parsed.shift !== e.shiftKey) return false;
  if (parsed.alt !== e.altKey) return false;

  const eventKey = e.key.toLowerCase();

  // Map special key names
  const keyMap: Record<string, string> = {
    escape: 'esc',
    enter: 'enter',
    '/': '/',
    '?': '?',
    f11: 'f11',
    f: 'f',
    b: 'b',
    i: 'i',
    u: 'u',
    k: 'k',
    s: 's',
    '1': '1',
    '2': '2',
    '3': '3',
    '4': '4',
    '5': '5',
  };

  const normalizedEvent = keyMap[eventKey] ?? eventKey;
  return normalizedEvent === parsed.key;
}

function isInputFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
}

export function useKeyboardShortcuts(shortcuts: ShortcutDef[]) {
  // Keep a stable ref to avoid re-registering on every render
  const shortcutsRef = useRef<ShortcutDef[]>(shortcuts);
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      for (const shortcut of shortcutsRef.current) {
        const parsed = parseCombo(shortcut.combo);

        if (!matchesEvent(parsed, e)) continue;

        // Check optional guard
        if (shortcut.when && !shortcut.when()) continue;

        // Input focus guard:
        // - combos with 'mod' modifier bypass this (Ctrl/Cmd combos)
        // - 'esc' bypasses this
        // - allowInInputs bypasses this
        const bypassInputGuard =
          parsed.mod ||
          parsed.key === 'esc' ||
          shortcut.allowInInputs;

        if (!bypassInputGuard && isInputFocused()) continue;

        e.preventDefault();
        shortcut.handler(e);
        return; // first matching shortcut wins
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: false });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: false });
  }, []); // stable ref — no deps needed
}
